import React, { useEffect, useState } from "react";
import { Form, Select, Input, Button, Col, Row, DatePicker, Checkbox } from "antd";
import moment from "moment";
import styles from "./adSearchForm.scss";
import { ModulesI } from "../../../typings/routesTypes";
import { categoriesInterface } from "../../../typings/interfaces";
import { AD_DUPLICATE_TAB_KEY, AdFilterDateTypeText, CategoryName, EntityType } from "../../../models";
import { useMakes } from "../../../routes/Configuration/hooks";
import config from "appConfig";

// Categories whose ads carry a make / model (configuration/makes?category=)
const CATEGORIES_WITH_MAKES = [
  CategoryName.car,
  CategoryName.trailers,
  CategoryName.buses,
  CategoryName.motorbike,
  CategoryName.commercialVehicle
];

interface AdSearchFormProps {
  form: any;
  data?: any;
  modules: ModulesI[];
  categories: categoriesInterface[];
  className: string;
  onSubmit: (param: any) => void;
}

const AdSearchForm = (props: AdSearchFormProps) => {
  const { onSubmit, className, form, modules, categories, data } = props;

  useEffect(() => {
    if (data) {
      // Rehydrate DatePicker fields into moment (local time) without mutating
      // `data`. `data` is a redux/ref reference shared with `searchCriteriaRef`
      // in the HOC — mutating it would leak the moment into subsequent
      // pagination requests and BE would receive a serialized number.
      const rehydrated = {
        ...data,
        dateFrom: data.dateFrom ? moment(data.dateFrom, "YYYY-MM-DD") : undefined,
        dateTo: data.dateTo ? moment(data.dateTo, "YYYY-MM-DD") : undefined,
      };

      form.setFieldsValue(rehydrated);
    }

  }, [data]);

  // the duplicates tab is keyed by a client-only value, not by an ad status
  const isDuplicateTab = new URLSearchParams(window.location.search).get("status") === AD_DUPLICATE_TAB_KEY;

  // Send date-only strings to BE so day boundaries stay in local time.
  // Previously the raw moment was serialized as a full UTC ISO string, which
  // shifted the range by the TZ offset and pulled in ads from the next day.
  const formatDateOnly = (value: any) =>
    value && moment.isMoment(value) ? value.format("YYYY-MM-DD") : value;

  const handleSubmit = e => {
    e.preventDefault();
    const values = form.getFieldsValue();
    onSubmit({
      ...values,
      dateFrom: formatDateOnly(values.dateFrom),
      dateTo: formatDateOnly(values.dateTo),
    });
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ makeId: null, modelId: null });
    setShowMakeFilter(false);
    setShowModelFilter(false);
    onSubmit(form.getFieldsValue());
  };

  const [selectedCategories, setSelectedCategories] = useState(null);

  const [selectedModels, setSelectedModels] = useState(null);

  const [showMakeFilter, setShowMakeFilter] = useState(false);

  const [showModelFilter, setShowModelFilter] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);

  // makes (with models) of the selected category, fetched on demand
  const selectedMakes = useMakes(selectedCategory) || null;

  const handleModuleChange = e => {
    setSelectedCategories(categories.filter(category => category.moduleName === e));
    setSelectedCategory(null);
    setSelectedModels(null);
    setShowMakeFilter(false);
    setShowModelFilter(false);
    form.setFieldsValue({ category: null, makeId: null, modelId: null });
  };

  const handleCategoryChange = e => {
    form.setFieldsValue({ makeId: null, modelId: null });
    setShowModelFilter(false);
    setSelectedModels(null);
    if (CATEGORIES_WITH_MAKES.includes(e)) {
      setSelectedCategory(e);
      setShowMakeFilter(true);
    } else {
      setSelectedCategory(null);
      setShowMakeFilter(false);
    }
  }

  const handleMakeChange = e => {
    setShowModelFilter(false);
    form.setFieldsValue({ makeId: null, modelId: null });
    if (e !== null) {
      const topLevel = selectedMakes || [];
      const selectedMake = topLevel.find(child => child.id === e);
      const bottomLevel = (selectedMake && selectedMake.models) || [];
      if (bottomLevel.length !== 0) {
        setSelectedModels(bottomLevel);
        form.setFieldsValue({ modelId: null });
        setShowModelFilter(true);
      } else {
        setSelectedModels(null);
        form.setFieldsValue({ modelId: null, makeId: null })
        setShowModelFilter(false);
      }
    }
  }

  const handlePromotionsChange = (e) => {
    if (e !== null) {
      form.setFieldsValue({ isPromoted: e.includes("promoted") }) // send true or false
      form.setFieldsValue({ autoPromotionStatus: e.includes("auto-promotion") ? 1 : 0 })
      form.setFieldsValue({ hasFreePromotion: e.includes("free-promotion") ? true : null })
    }
  }

  const handleOnlyConfirmedDuplicatesChange = (isChecked) => {
    if (isChecked) {
      form.setFieldsValue({ isDuplicate: true })
    } else {
      form.setFieldsValue({ isDuplicate: null })
    }
  }

  const handleOnlyPricechangeChange = (isChecked) => {
    if (isChecked) {
      form.setFieldsValue({ isPriceChanged: true })
    } else {
      form.setFieldsValue({ isPriceChanged: null })
    }
  }

  return (
    <Row gutter={2} className={styles.form}>
      <Form
        layout="horizontal"
        onSubmit={handleSubmit}
      >
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("code")(
              <Input placeholder={"Kod"} />
            )}
          </Form.Item>
        </Col>
        <Form.Item style={{ display: "none" }}>
          {form.getFieldDecorator("status")(<Input type="hidden" />)}
        </Form.Item>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("text")(
              <Input placeholder={"Naslov ili opis"} />
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("creatorEmail")(
              <Input placeholder={"Email"} />
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("module")(
              <Select placeholder={"Kategorija"} onChange={handleModuleChange}>
                <Select.Option key={0} value={null}>{"Poništi"}</Select.Option>
                {modules
                  ? modules.map(module => (
                    <Select.Option key={module.name} value={module.name}>
                      {module.displayName}
                    </Select.Option>
                  ))
                  : null}
              </Select>
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("category")(
              <Select placeholder={"Potkategorija"} onChange={handleCategoryChange}>
                <Select.Option key={0} value={null}>{"Poništi"}</Select.Option>
                {selectedCategories
                  ? selectedCategories.map(category => (
                    <Select.Option key={category.name} value={category.name}>
                      {category.displayName}
                    </Select.Option>
                  ))
                  : null}
              </Select>
            )}
          </Form.Item>
        </Col>
        {showMakeFilter && (
          <Col md={12} sm={24} lg={3} xl={3}>
            <Form.Item className={styles.gutterBox}>
              {form.getFieldDecorator("makeId")(
                <Select placeholder={"Odaberite model"} onChange={handleMakeChange}>
                  <Select.Option key={0} value={null}>{"Poništi"}</Select.Option>
                  {selectedMakes
                    ? selectedMakes.map(make => (
                      <Select.Option key={make.id} value={make.id}>{make.name}</Select.Option>
                    ))
                    : null}
                </Select>
              )}
            </Form.Item>
          </Col>
        )}
        {showModelFilter && (
          <Col md={12} sm={24} lg={3} xl={3}>
            <Form.Item className={styles.gutterBox}>
              {form.getFieldDecorator("modelId")(
                <Select placeholder={"Odaberite marku"}>
                  <Select.Option key={0} value={null}>{"Poništi"}</Select.Option>
                  {selectedModels
                    ? selectedModels.map(model => (
                      <Select.Option key={model.id} value={model.id}>{model.name}</Select.Option>
                    ))
                    : null}
                </Select>
              )}
            </Form.Item>
          </Col>
        )}
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("legalEntity")(
              <Select placeholder={"Tip prodavača"}>
                <Select.Option value={null} key={0}>{"Tip prodavača"}</Select.Option>
                <Select.Option value={EntityType.PrivatePerson} key={1}>{"Fizička osoba"}</Select.Option>
                <Select.Option value={EntityType.LegalEntity} key={2}>{"Pravna osoba"}</Select.Option>
              </Select>
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("dateFrom")(
              <DatePicker placeholder={"Datum od"} format={config.dateFormat} />
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("dateTo")(
              <DatePicker placeholder={"Datum do"} format={config.dateFormat} />
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("dateType")(
              <Select placeholder={"Tip datuma"}>
                {AdFilterDateTypeText.map((item, index) => (
                  <Select.Option value={item.value.toString()} key={index}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={4} xl={4}>
          <Form.Item className={styles.gutterBox}>
            <Select
              placeholder={"Promocije"}
              mode="multiple"
              className={styles.multipleSelect}
              onChange={handlePromotionsChange}
            >
              <Select.Option value={"promoted"} key={0}>{"Istaknut u pretrazi"}</Select.Option>
              <Select.Option value={"auto-promotion"} key={1}>{"Uključeno automatsko resiticanje"}</Select.Option>
              <Select.Option value={"free-promotion"} key={2}>Besplatna promocija</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={3} xl={3}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("isAgent")(
              <Select placeholder={"Svi oglasi"} mode="default">
                <Select.Option value={null} key={0}>Svi oglasi</Select.Option>
                <Select.Option value={"1"} key={1}>Samo XML oglasi</Select.Option>
                <Select.Option value={"2"} key={2}>Svi sem XML oglasa</Select.Option>
              </Select>
            )}
          </Form.Item>
        </Col>
        <Form.Item style={{ display: "none" }}>
          {form.getFieldDecorator("isPromoted")(<Input type="hidden" />)}
        </Form.Item>
        <Form.Item style={{ display: "none" }}>
          {form.getFieldDecorator("autoPromotionStatus")(<Input type="hidden" />)}
        </Form.Item>
        <Form.Item style={{ display: "none" }}>
          {form.getFieldDecorator("hasFreePromotion")(<Input type="hidden" />)}
        </Form.Item>
        {isDuplicateTab && (
          <Col md={12} sm={24} lg={3} xl={3}>
            <Form.Item className={styles.gutterBox}>
              {form.getFieldDecorator("isDuplicate", {
                valuePropName: "checked",
                initialValue: null
              })(
                <Checkbox onChange={(e) => { handleOnlyConfirmedDuplicatesChange(e.target.checked) }}>
                  Samo potvrđeni duplikati
                </Checkbox>
              )}
            </Form.Item>
          </Col>
        )}
        <Col md={12} sm={24} lg={4} xl={4}>
          <Form.Item className={styles.gutterBox}>
            {form.getFieldDecorator("isPriceChanged", {
              valuePropName: "checked",
              initialValue: null
            })(
              <Checkbox onChange={(e) => { handleOnlyPricechangeChange(e.target.checked) }}>
                Promijenjena cijena
              </Checkbox>
            )}
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={2} xl={2}>
          <Form.Item className={styles.gutterBox}>
            <Button type="primary" htmlType="submit" className={styles.actionButton}>
              {"Traži"}
            </Button>
          </Form.Item>
        </Col>
        <Col md={12} sm={24} lg={2} xl={2}>
          <Form.Item className={styles.gutterBox}>
            <Button onClick={handleReset} className={styles.actionButton}>
              {"Resetiraj"}
            </Button>
          </Form.Item>
        </Col>
      </Form>
    </Row>
  );
};

AdSearchForm.defaultProps = {
  className: "",
  modules: [],
  categories: []
};

export default Form.create()(AdSearchForm);



// WEBPACK FOOTER //
// ./src/routes/Admin/AdSearchForm/AdSearchForm.tsx